/**
 * SupportQueuePage — admin-facing ticket queue.
 *
 * Admins can:
 *   - See all tickets with filters (status, category, priority, assignee)
 *   - Search by ticket number, subject, or requester name
 *   - Open a ticket to read the full thread and reply
 *   - Change status, priority, and assignee
 */

import { useEffect, useState } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { ticketAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';
import { ProfileCard, Pill } from '../components/profile/ProfilePrimitives';

const BORDER = '#ddd6c6';
const TEXT = '#08111f';
const MUTED = '#7a7060';

const STATUS_TONES = {
  open: 'orange',
  in_progress: 'teal',
  resolved: 'green',
  closed: 'neutral',
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_TONES = {
  low: 'neutral',
  medium: 'orange',
  high: 'red',
  urgent: 'purple',
};

const CATEGORIES = [
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'account', label: 'Account' },
];

// Stale-while-revalidate (services/viewCache): this route unmounts on nav, so
// the queue list flashed a loading card over tickets just seen. The key folds
// in every filter; the detail view keeps its own load (it opens a fresh ticket).
const DEFAULT_QUEUE_FILTERS = { status: '', category: '', priority: '', assignee: '', search: '' };
const queueKey = (f) => `support:queue:${f.status}|${f.category}|${f.priority}|${f.assignee}|${f.search}`;

function TicketDetail({ ticketId, onBack, onUpdated }) {
  const { user } = useAuth();
  const { showToast } = useUI();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [updating, setUpdating] = useState(false);

  const fetchTicket = async () => {
    setLoading(true);
    try {
      const res = await ticketAPI.getTicket(ticketId);
      if (res.success) setTicket(res.data.ticket);
    } catch (err) {
      showToast('Could not load ticket', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      const res = await ticketAPI.addReply(ticketId, reply.trim());
      if (res.success) {
        setReply('');
        setTicket(res.data.ticket);
        showToast('Reply sent', 'success');
        onUpdated();
      }
    } catch (err) {
      showToast('Could not send reply', 'error');
    } finally {
      setSending(false);
    }
  };

  const updateField = async (updates) => {
    setUpdating(true);
    try {
      const res = await ticketAPI.updateTicket(ticketId, updates);
      if (res.success) {
        setTicket(res.data.ticket);
        showToast('Ticket updated', 'success');
        onUpdated();
      }
    } catch (err) {
      showToast('Could not update ticket', 'error');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <ProfileCard title="Loading…" icon="⏳">
        <div className="text-[13px] py-6" style={{ color: MUTED }}>Loading ticket details…</div>
      </ProfileCard>
    );
  }

  if (!ticket) {
    return (
      <ProfileCard title="Not Found" icon="❓">
        <div className="text-[13px] py-6" style={{ color: MUTED }}>Ticket not found.</div>
        <button onClick={onBack} className="text-[12px] font-medium" style={{ color: '#145f5f' }}>← Back</button>
      </ProfileCard>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="text-[12px] font-medium px-2 py-1 rounded" style={{ color: '#145f5f', border: `1px solid ${BORDER}` }}>
          ← Back to Queue
        </button>
        <span className="text-[11px] font-mono" style={{ color: MUTED }}>{ticket.ticketNumber}</span>
      </div>

      {/* Ticket header + management */}
      <ProfileCard title={ticket.subject} subtitle={`${CATEGORIES.find(c => c.value === ticket.category)?.label || ticket.category} — ${ticket.requesterName || 'Unknown'}`} icon="🎫">
        {/* Management bar */}
        <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b" style={{ borderColor: BORDER }}>
          <div className="flex items-center gap-2">
            <span className="text-[10.5px] uppercase tracking-[1px] font-semibold" style={{ color: MUTED }}>Status</span>
            <select
              value={ticket.status}
              onChange={(e) => updateField({ status: e.target.value })}
              disabled={updating}
              className="px-2 py-1 rounded border text-[11px] bg-white outline-none"
              style={{ borderColor: BORDER, color: TEXT }}
            >
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10.5px] uppercase tracking-[1px] font-semibold" style={{ color: MUTED }}>Priority</span>
            <select
              value={ticket.priority}
              onChange={(e) => updateField({ priority: e.target.value })}
              disabled={updating}
              className="px-2 py-1 rounded border text-[11px] bg-white outline-none"
              style={{ borderColor: BORDER, color: TEXT }}
            >
              {['low', 'medium', 'high', 'urgent'].map((p) => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10.5px] uppercase tracking-[1px] font-semibold" style={{ color: MUTED }}>Assignee</span>
            <select
              value={ticket.assignee?._id || ''}
              onChange={(e) => updateField({ assignee: e.target.value || null })}
              disabled={updating}
              className="px-2 py-1 rounded border text-[11px] bg-white outline-none"
              style={{ borderColor: BORDER, color: TEXT }}
            >
              <option value="">Unassigned</option>
              <option value={user?._id}>{user?.fullName} (Me)</option>
            </select>
          </div>
        </div>

        <div className="text-[13px] leading-relaxed" style={{ color: TEXT }}>
          {ticket.description}
        </div>
        <div className="mt-3 flex flex-wrap gap-3 text-[10.5px]" style={{ color: MUTED }}>
          <span>Created {new Date(ticket.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          <span>Requester: {ticket.requester?.fullName || ticket.requesterName} ({ticket.requester?.email || ticket.requesterEmail})</span>
          <span>Role: {ticket.requester?.roleName || '—'}</span>
        </div>
      </ProfileCard>

      {/* Replies */}
      <ProfileCard title="Conversation" subtitle={`${ticket.replies?.length || 0} message(s)`} icon="💬">
        <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
          {(!ticket.replies || ticket.replies.length === 0) && (
            <div className="text-[12px] py-4 text-center" style={{ color: MUTED }}>
              No replies yet.
            </div>
          )}
          {ticket.replies?.map((r, i) => (
            <div
              key={i}
              className="rounded-lg p-4"
              style={{
                background: r.authorRole === 'agent' || r.authorRole === 'admin' ? '#f0f7f7' : '#fdfbf6',
                border: `1px solid ${BORDER}`,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-[12px]" style={{ color: TEXT }}>
                  {r.author?.fullName || 'Unknown'}
                </span>
                {(r.authorRole === 'agent' || r.authorRole === 'admin') && (
                  <Pill tone="teal">Team</Pill>
                )}
                <span className="text-[10.5px] ml-auto" style={{ color: MUTED }}>
                  {new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="text-[12.5px] whitespace-pre-wrap" style={{ color: TEXT }}>
                {r.message}
              </div>
            </div>
          ))}
        </div>

        {/* Reply form */}
        <form onSubmit={sendReply} className="mt-4 pt-4 border-t" style={{ borderColor: BORDER }}>
          <div className="text-[10.5px] uppercase tracking-[1px] font-semibold mb-[5px]" style={{ color: MUTED }}>
            Reply as Team
          </div>
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write your response…"
            rows={3}
            className="w-full px-3 py-2 rounded-lg border text-[13px] bg-white outline-none focus:border-[#145f5f] resize-none"
            style={{ borderColor: BORDER, color: TEXT }}
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="submit"
              disabled={sending || !reply.trim()}
              className="px-3 py-[6px] rounded-lg text-[11px] font-semibold text-white disabled:opacity-60"
              style={{ background: '#145f5f' }}
            >
              {sending ? 'Sending…' : 'Send Reply'}
            </button>
          </div>
        </form>
      </ProfileCard>
    </div>
  );
}

export default function SupportQueuePage() {
  const { showToast } = useUI();
  // Seed from cache for the default (no-filter) view so a revisit paints instantly.
  const seeded = getView(queueKey(DEFAULT_QUEUE_FILTERS));
  const [tickets, setTickets] = useState(() => seeded?.tickets ?? []);
  const [counts, setCounts] = useState(() => seeded?.counts ?? { all: 0, open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [filters, setFilters] = useState(DEFAULT_QUEUE_FILTERS);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [loading, setLoading] = useState(() => !hasView(queueKey(DEFAULT_QUEUE_FILTERS)));

  const fetchTickets = async () => {
    const key = queueKey(filters);
    if (!hasView(key)) setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.priority) params.priority = filters.priority;
      if (filters.assignee) params.assignee = filters.assignee;
      if (filters.search) params.search = filters.search;

      const res = await ticketAPI.getAllTickets(params);
      if (res.success) {
        setTickets(res.data.tickets);
        setCounts(res.data.counts);
        setView(key, { tickets: res.data.tickets, counts: res.data.counts });
      }
    } catch (err) {
      // Keep the cached list on a failed refresh; toast only on a first load.
      if (!hasView(key)) showToast('Could not load tickets', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [filters.status, filters.category, filters.priority, filters.assignee]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTickets();
    }, 400);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const statusCounts = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Open' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'resolved', label: 'Resolved' },
    { key: 'closed', label: 'Closed' },
  ];

  return (
    <AppLayout pageTitle="Support Queue">
      <div className="text-[26px] font-bold text-gray-900 mb-[6px]" style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '.2px' }}>
        Support Queue
      </div>
      <div className="h-[2px] w-[64px] mb-3" style={{ background: 'linear-gradient(90deg,#b8973a 0%,transparent 100%)', borderRadius: 2 }} />
      <div className="text-[13px] text-gray-500 mb-6">
        Manage and respond to all support tickets.
      </div>

      {selectedTicketId ? (
        <TicketDetail
          ticketId={selectedTicketId}
          onBack={() => setSelectedTicketId(null)}
          onUpdated={fetchTickets}
        />
      ) : (
        <ProfileCard title="All Tickets" subtitle={`${counts.all} total`} icon="🎫">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            {statusCounts.map((s) => (
              <button
                key={s.key}
                onClick={() => setFilters(f => ({ ...f, status: s.key === 'all' ? '' : s.key }))}
                className="px-3 py-[5px] rounded-lg text-[11px] font-medium transition-colors"
                style={{
                  background: (filters.status === s.key || (s.key === 'all' && !filters.status)) ? '#145f5f' : '#f8f5ef',
                  color: (filters.status === s.key || (s.key === 'all' && !filters.status)) ? '#fff' : MUTED,
                  border: `1px solid ${(filters.status === s.key || (s.key === 'all' && !filters.status)) ? '#145f5f' : BORDER}`,
                }}
              >
                {s.label} ({counts[s.key] || 0})
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
            <div>
              <div className="text-[10.5px] uppercase tracking-[1px] font-semibold mb-[5px]" style={{ color: MUTED }}>Category</div>
              <select
                value={filters.category}
                onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-[13px] bg-white outline-none focus:border-[#145f5f]"
                style={{ borderColor: BORDER, color: TEXT }}
              >
                <option value="">All</option>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[1px] font-semibold mb-[5px]" style={{ color: MUTED }}>Priority</div>
              <select
                value={filters.priority}
                onChange={(e) => setFilters(f => ({ ...f, priority: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-[13px] bg-white outline-none focus:border-[#145f5f]"
                style={{ borderColor: BORDER, color: TEXT }}
              >
                <option value="">All</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[1px] font-semibold mb-[5px]" style={{ color: MUTED }}>Assignee</div>
              <select
                value={filters.assignee}
                onChange={(e) => setFilters(f => ({ ...f, assignee: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-[13px] bg-white outline-none focus:border-[#145f5f]"
                style={{ borderColor: BORDER, color: TEXT }}
              >
                <option value="">All</option>
                <option value="me">Assigned to me</option>
                <option value="unassigned">Unassigned</option>
              </select>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[1px] font-semibold mb-[5px]" style={{ color: MUTED }}>Search</div>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Ticket #, subject, name…"
                className="w-full px-3 py-2 rounded-lg border text-[13px] bg-white outline-none focus:border-[#145f5f]"
                style={{ borderColor: BORDER, color: TEXT }}
              />
            </div>
          </div>

          {loading ? (
            <div className="text-[13px] py-6" style={{ color: MUTED }}>Loading tickets…</div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-10 text-[13px]" style={{ color: MUTED }}>
              No tickets match your filters.
            </div>
          ) : (
            <div className="space-y-2">
              {/* Table header */}
              <div className="hidden sm:grid grid-cols-12 gap-3 px-3 py-2 text-[10.5px] uppercase tracking-[1px] font-semibold" style={{ color: MUTED }}>
                <div className="col-span-2">Ticket #</div>
                <div className="col-span-3">Subject</div>
                <div className="col-span-2">Requester</div>
                <div className="col-span-1">Category</div>
                <div className="col-span-1">Priority</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-1">Assignee</div>
                <div className="col-span-1 text-right">Created</div>
              </div>

              {tickets.map((ticket) => (
                <button
                  key={ticket._id}
                  onClick={() => setSelectedTicketId(ticket._id)}
                  className="w-full text-left border rounded-lg p-3 transition-colors hover:bg-[#faf8f3]"
                  style={{ borderColor: BORDER }}
                >
                  <div className="sm:hidden space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-mono" style={{ color: MUTED }}>{ticket.ticketNumber}</span>
                      <Pill tone={STATUS_TONES[ticket.status] || 'neutral'}>{STATUS_LABELS[ticket.status]}</Pill>
                    </div>
                    <div className="font-semibold text-[13px]" style={{ color: TEXT }}>{ticket.subject}</div>
                    <div className="text-[11px]" style={{ color: MUTED }}>{ticket.requesterName || ticket.requester?.fullName}</div>
                  </div>

                  <div className="hidden sm:grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-2 text-[11px] font-mono" style={{ color: MUTED }}>{ticket.ticketNumber}</div>
                    <div className="col-span-3 text-[12px] font-medium truncate" style={{ color: TEXT }}>{ticket.subject}</div>
                    <div className="col-span-2 text-[11px] truncate" style={{ color: MUTED }}>{ticket.requesterName || ticket.requester?.fullName}</div>
                    <div className="col-span-1 text-[11px]" style={{ color: MUTED }}>{ticket.category}</div>
                    <div className="col-span-1">
                      <Pill tone={PRIORITY_TONES[ticket.priority] || 'neutral'}>{ticket.priority}</Pill>
                    </div>
                    <div className="col-span-1">
                      <Pill tone={STATUS_TONES[ticket.status] || 'neutral'}>{STATUS_LABELS[ticket.status]}</Pill>
                    </div>
                    <div className="col-span-1 text-[11px] truncate" style={{ color: MUTED }}>{ticket.assigneeName || '—'}</div>
                    <div className="col-span-1 text-right text-[10.5px]" style={{ color: MUTED }}>
                      {new Date(ticket.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ProfileCard>
      )}
    </AppLayout>
  );
}
